import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { DashboardLayout } from '../../components/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { BarcodeScanner } from '../../components/BarcodeScanner';
import { formatDateTimeShort } from '../../utils/dateFormat';
import {
  Truck, Plus, Edit2, Trash2, Upload, Package,
  AlertTriangle, CheckCircle, Clock, X, Building2,
  FileText, MapPin, Hash, AlertCircle, RefreshCw, Eye, MoreVertical,
  Mail, Copy, Check, ShoppingBag, Search, BarChart2, AlertOctagon,
  Zap, ScanLine, ChevronRight, ArrowRight, Activity, XCircle, SkipForward, Play, Download, Calendar, RotateCcw,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// ─── Status-Badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    imported:    { label: 'Importiert',     cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
    in_progress: { label: 'In Bearbeitung', cls: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300' },
    completed:   { label: 'Abgeschlossen',  cls: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
  };
  const { label, cls } = map[status] || { label: status, cls: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300' };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{label}</span>;
};

// ─── Kategorie-Label ──────────────────────────────────────────────────────────
const CatLabel = ({ cat }) => {
  const map = {
    tabak:           { label: 'Tabak',              cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
    shop:            { label: 'Shop',               cls: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300' },
    frische_backshop:{ label: 'Frische & Backshop', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
    mixed:           { label: 'Gemischt',           cls: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' },
  };
  const { label, cls } = map[cat] || { label: cat, cls: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300' };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{label}</span>;
};

// ─── Modal-Wrapper ────────────────────────────────────────────────────────────
const Modal = ({ title, onClose, children, size = 'md' }) => {
  const sizeMap = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl' };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className={`relative w-full ${sizeMap[size]} bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-h-[90vh] flex flex-col`}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-4">{children}</div>
      </div>
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

const Input = ({ className = '', ...props }) => (
  <input
    className={`w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors ${className}`}
    {...props}
  />
);

// ─── Lieferanten-Formular ─────────────────────────────────────────────────────
const SupplierForm = ({ initial, locations, currentUserName, onSave, onClose, saving }) => {
  const isEdit = !!initial?.id;
  const [name, setName] = useState(initial?.name || '');
  const [customerNumber, setCustomerNumber] = useState(initial?.customer_number || '');
  const [deliveryName, setDeliveryName] = useState(initial?.delivery_name || currentUserName || '');
  const [locationId, setLocationId] = useState(initial?.location_id || (locations.length === 1 ? locations[0].id : ''));
  const [complaintEmail, setComplaintEmail] = useState(initial?.complaint_email || '');
  const [errors, setErrors] = useState({});

  const selectedLoc = locations.find(l => l.id === locationId);

  const validate = () => {
    const e = {};
    if (!name.trim())           e.name = 'Pflichtfeld';
    if (!customerNumber.trim()) e.customerNumber = 'Pflichtfeld';
    if (!deliveryName.trim())   e.deliveryName = 'Pflichtfeld';
    if (!locationId)            e.locationId = 'Bitte Standort auswählen';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (validate()) onSave({ name, customer_number: customerNumber, delivery_name: deliveryName, location_id: locationId, complaint_email: complaintEmail });
  };

  const locAddress = selectedLoc
    ? `${selectedLoc.street || ''} ${selectedLoc.house_number || ''}`.trim() + `, ${selectedLoc.postal_code || ''} ${(selectedLoc.city || '').toUpperCase()}`
    : null;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Standort-Auswahl */}
      <Field label="Standort" required>
        {isEdit ? (
          <div className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-sm text-gray-600 dark:text-gray-400">
            {locations.find(l => l.id === locationId)?.name || locationId}
          </div>
        ) : (
          <select
            value={locationId}
            onChange={e => setLocationId(e.target.value)}
            className={`w-full px-3 py-2 rounded-lg border ${errors.locationId ? 'border-red-400' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/50`}
            data-testid="supplier-location-select"
          >
            <option value="">Standort auswählen...</option>
            {locations.map(l => (
              <option key={l.id} value={l.id}>
                {l.name}{l.station_number ? ` (Nr. ${l.station_number})` : ''}
              </option>
            ))}
          </select>
        )}
        {errors.locationId && <p className="mt-1 text-xs text-red-500">{errors.locationId}</p>}
      </Field>

      {/* Adress-Vorschau aus Standort */}
      {selectedLoc && (
        <div className="flex items-start gap-2 px-3 py-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-sm" data-testid="location-address-preview">
          <MapPin className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
          <div className="text-blue-700 dark:text-blue-300">
            <p className="font-medium">{selectedLoc.name}{selectedLoc.station_number ? ` · Nr. ${selectedLoc.station_number}` : ''}</p>
            <p className="text-xs mt-0.5">{locAddress}</p>
            <p className="text-xs text-blue-500 dark:text-blue-400 mt-1">Adresse wird automatisch für den Doppel-Check verwendet</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Lieferantenname" required>
          <Input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="z.B. Lekkerland SE"
            className={errors.name ? 'border-red-400' : ''}
            data-testid="supplier-name-input"
          />
          {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
        </Field>
        <Field label="Kundennummer (KD)" required>
          <Input
            value={customerNumber}
            onChange={e => setCustomerNumber(e.target.value)}
            placeholder="z.B. 40676176"
            className={errors.customerNumber ? 'border-red-400' : ''}
            data-testid="supplier-customer-number-input"
          />
          {errors.customerNumber && <p className="mt-1 text-xs text-red-500">{errors.customerNumber}</p>}
        </Field>
      </div>

      <Field label="Empfänger-Name (auf dem Lieferschein)" required>
        <Input
          value={deliveryName}
          onChange={e => setDeliveryName(e.target.value)}
          placeholder="z.B. Ali Tombul"
          className={errors.deliveryName ? 'border-red-400' : ''}
          data-testid="supplier-delivery-name-input"
        />
        {errors.deliveryName && <p className="mt-1 text-xs text-red-500">{errors.deliveryName}</p>}
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Name wie er auf dem PDF-Lieferschein steht</p>
      </Field>

      <Field label="Reklamations-E-Mail">
        <Input
          type="email"
          value={complaintEmail}
          onChange={e => setComplaintEmail(e.target.value)}
          placeholder="z.B. kundenservice.stuttgart@lekkerland.com"
          data-testid="supplier-complaint-email-input"
        />
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
          Wird beim Senden der Reklamation automatisch als Empfänger eingetragen
        </p>
      </Field>

      <div className="flex justify-end gap-3 pt-2 border-t border-gray-100 dark:border-gray-800">
        <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
          Abbrechen
        </button>
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 rounded-lg text-sm font-medium bg-primary text-white hover:opacity-90 disabled:opacity-50 transition-all"
          data-testid="supplier-save-btn"
        >
          {saving ? 'Wird gespeichert...' : 'Speichern'}
        </button>
      </div>
    </form>
  );
};

// ─── Import-Log Status-Badge ──────────────────────────────────────────────────
const ImportLogStatusBadge = ({ status }) => {
  const map = {
    success: { label: 'Erfolg',              cls: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',   Icon: CheckCircle },
    error:   { label: 'Fehler',              cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',           Icon: XCircle },
    skipped: { label: 'Übersprungen',        cls: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',          Icon: SkipForward },
    info:    { label: 'Info (übersprungen)', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',       Icon: Activity },
  };
  const { label, cls, Icon } = map[status] || { label: status, cls: 'bg-gray-100 text-gray-600', Icon: AlertCircle };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      <Icon className="h-3 w-3" />{label}
    </span>
  );
};

const MatchMethodBadge = ({ method }) => {
  if (!method) return null;
  const map = {
    customer_number:  { label: 'Kd-Nr.',   cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300', title: 'Zuordnung via Kundennummer aus dem PDF' },
    delivery_zip:     { label: 'PLZ',       cls: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300',             title: 'Zuordnung via Liefer-PLZ aus dem PDF' },
    delivery_address: { label: 'PLZ+Str.',  cls: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',             title: 'Zuordnung via PLZ + Straße aus dem PDF' },
    import_token:     { label: 'Token',     cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',         title: 'Zuordnung via Import-Token (Fallback)' },
  };
  const cfg = map[method] || { label: method, cls: 'bg-gray-100 text-gray-600', title: method };
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${cfg.cls}`} title={cfg.title}>
      {cfg.label}
    </span>
  );
};

// ─── Lieferanten-Karte ────────────────────────────────────────────────────────
const SupplierCard = ({ supplier, locationName, onEdit, onDelete }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const menuRef = React.useRef();

  const importEmail = supplier.import_token
    ? `import-${supplier.import_token}@allflowx.de`
    : null;

  const handleCopyEmail = async () => {
    if (!importEmail) return;
    try {
      await navigator.clipboard.writeText(importEmail);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = importEmail;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Schließen bei Klick außerhalb
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => { document.removeEventListener('mousedown', handler); document.removeEventListener('touchstart', handler); };
  }, [menuOpen]);

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-4 hover:border-primary/40 transition-all" data-testid="supplier-card">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Truck className="h-4 w-4 text-primary" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-gray-900 dark:text-white text-sm truncate">{supplier.name}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">KD-Nr: {supplier.customer_number}</p>
          </div>
        </div>
        {/* Drei-Punkt-Menü – immer sichtbar, touch-freundlich */}
        <div className="relative flex-shrink-0" ref={menuRef}>
          <button
            onClick={() => setMenuOpen(v => !v)}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            aria-label="Optionen"
            data-testid="supplier-menu-btn"
          >
            <MoreVertical className="h-4 w-4" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-full mt-1 w-36 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-20 overflow-hidden">
              <button
                onClick={() => { setMenuOpen(false); onEdit(supplier); }}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                data-testid="supplier-edit-btn"
              >
                <Edit2 className="h-4 w-4 text-primary" />
                Bearbeiten
              </button>
              <button
                onClick={() => { setMenuOpen(false); onDelete(supplier); }}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                data-testid="supplier-delete-btn"
              >
                <Trash2 className="h-4 w-4" />
                Löschen
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="space-y-1.5">
        <div className="flex items-start gap-1.5 text-xs text-gray-600 dark:text-gray-400">
          <MapPin className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          <span>{supplier.delivery_street}, {supplier.delivery_zip} {supplier.delivery_city}</span>
        </div>
        {supplier.delivery_station && (
          <div className="flex items-start gap-1.5 text-xs text-gray-500 dark:text-gray-500">
            <Building2 className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <span>{supplier.delivery_station}</span>
          </div>
        )}
        {locationName && (
          <div className="flex items-start gap-1.5 text-xs text-gray-500 dark:text-gray-500">
            <Hash className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <span>{locationName}</span>
          </div>
        )}
        {/* Import-E-Mail-Adresse */}
        {importEmail && (
          <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-800">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              PDF-Import per E-Mail
            </p>
            <button
              onClick={handleCopyEmail}
              title="Adresse kopieren – diese Adresse an den Lieferanten weitergeben"
              className="w-full flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors group"
              data-testid="supplier-import-email-copy"
            >
              <Mail className="h-3 w-3 text-blue-500 flex-shrink-0" />
              <span className="text-xs text-blue-700 dark:text-blue-300 font-mono truncate flex-1 text-left">
                {importEmail}
              </span>
              {copied
                ? <Check className="h-3 w-3 text-green-500 flex-shrink-0" />
                : <Copy className="h-3 w-3 text-blue-400 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
              }
            </button>
            <p className="text-xs text-muted-foreground mt-1">
              ↑ Diese Adresse an <strong>{supplier?.name || 'den Lieferanten'}</strong> weitergeben (nicht die aus Firmendaten)
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── PDF-Upload-Bereich ───────────────────────────────────────────────────────
const UploadArea = ({ onFile, loading }) => {
  const [drag, setDrag] = useState(false);
  const dragCounter = React.useRef(0);
  const fileRef = React.useRef();
  const handleFile = f => { if (f?.type === 'application/pdf') onFile(f); else if (f) toast.error('Nur PDF-Dateien werden akzeptiert'); };

  const handleDragEnter = e => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items?.length > 0) setDrag(true);
  };
  const handleDragLeave = e => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) setDrag(false);
  };
  const handleDragOver = e => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop = e => {
    e.preventDefault();
    e.stopPropagation();
    setDrag(false);
    dragCounter.current = 0;
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <div
      className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all duration-150 ${drag ? 'border-primary bg-primary/8 dark:bg-primary/15 scale-[1.01] shadow-md' : 'border-gray-300 dark:border-gray-600 hover:border-primary/50 hover:bg-gray-50 dark:hover:bg-gray-800/50'} ${loading ? 'opacity-60 pointer-events-none' : ''}`}
      onClick={() => fileRef.current?.click()}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      data-testid="pdf-upload-area"
    >
      <input ref={fileRef} type="file" accept=".pdf" className="hidden" onChange={e => handleFile(e.target.files[0])} data-testid="pdf-file-input" />
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3 transition-colors ${drag ? 'bg-primary/20' : 'bg-primary/10'}`}>
        {loading
          ? <RefreshCw className="h-6 w-6 text-primary animate-spin" />
          : drag
            ? <Upload className="h-6 w-6 text-primary animate-bounce" />
            : <Upload className="h-6 w-6 text-primary" />
        }
      </div>
      {loading ? (
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">PDF wird verarbeitet...</p>
      ) : drag ? (
        <>
          <p className="text-sm font-semibold text-primary mb-1">PDF jetzt loslassen</p>
          <p className="text-xs text-primary/70">Datei wird importiert</p>
        </>
      ) : (
        <>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">WBS-PDF hier ablegen oder klicken</p>
          <p className="text-xs text-gray-400 dark:text-gray-500">Aus dem Ordner direkt hierher ziehen – max. 20 MB</p>
        </>
      )}
    </div>
  );
};

// ─── Lieferungs-Zeile ─────────────────────────────────────────────────────────
const DeliveryRow = ({ delivery, onDelete, onDownloadPdf, onReopen, userRole, selectMode, selected, onSelect }) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = React.useRef();
  const pct = delivery.total_items > 0
    ? Math.round((delivery.checked_items / delivery.total_items) * 100)
    : 0;

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => { document.removeEventListener('mousedown', handler); document.removeEventListener('touchstart', handler); };
  }, [menuOpen]);

  const handleRowClick = () => {
    if (selectMode) { onSelect(delivery.id); } else { navigate(`/lieferung/${delivery.id}`); }
  };

  return (
    <div
      className={`flex flex-col sm:flex-row sm:items-center gap-3 p-4 bg-white dark:bg-gray-900 border rounded-xl transition-all group cursor-pointer ${selected ? 'border-primary bg-primary/5 dark:bg-primary/10' : 'border-gray-200 dark:border-gray-700 hover:border-primary/40'}`}
      onClick={handleRowClick}
      data-testid="delivery-row"
    >
      {selectMode && (
        <div className="flex items-center flex-shrink-0" onClick={e => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={!!selected}
            onChange={() => onSelect(delivery.id)}
            className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer accent-primary"
            data-testid="delivery-checkbox"
          />
        </div>
      )}
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
          <FileText className="h-4 w-4 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-0.5">
            <span className="font-semibold text-sm text-gray-900 dark:text-white">WBS {delivery.wbs_number || '—'}</span>
            <StatusBadge status={delivery.status} />
            {delivery.product_category && <CatLabel cat={delivery.product_category} />}
            {delivery.address_warning && (
              <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                <AlertTriangle className="h-3 w-3" /> Adress-Warnung
              </span>
            )}
            {delivery.ai_assisted && (
              <span className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-1.5 py-0.5 rounded-full font-medium">
                KI
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 flex flex-wrap gap-x-3 gap-y-0.5">
            <span>{delivery.supplier_name}</span>
            {delivery.delivery_date && <span>Lieferdatum: {delivery.delivery_date}</span>}
            <span>{delivery.checked_items}/{delivery.total_items} geprüft</span>
          </div>
          {delivery.total_items > 0 && (
            <div className="mt-1.5 h-1 bg-gray-200 dark:bg-gray-700 rounded-full w-32">
              <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${pct}%` }} />
            </div>
          )}
        </div>
      </div>
      {/* Drei-Punkt-Menü */}
      <div className="flex items-center gap-2 flex-shrink-0" onClick={e => e.stopPropagation()}>
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen(v => !v)}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 transition-colors"
            data-testid="delivery-menu-btn"
          >
            <MoreVertical className="h-4 w-4" />
          </button>
          {menuOpen && (
            <div className="absolute left-0 bottom-full mb-1 sm:left-auto sm:right-0 sm:bottom-auto sm:top-full sm:mt-1 w-44 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-20 overflow-hidden">
              <button
                onClick={() => { setMenuOpen(false); navigate(`/lieferung/${delivery.id}`); }}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                data-testid="delivery-open-btn"
              >
                <Eye className="h-4 w-4 text-primary" />
                Checkliste öffnen
              </button>
              {delivery.status === 'completed' && onReopen && ['admin', 'stationsleitung', 'superadmin'].includes(userRole) && (
                <button
                  onClick={() => { setMenuOpen(false); onReopen(delivery); }}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
                  data-testid="delivery-reopen-btn"
                >
                  <RotateCcw className="h-4 w-4" />
                  Wiedereröffnen
                </button>
              )}
              {delivery.imported_via === 'email' && onDownloadPdf && (
                <button
                  onClick={() => { setMenuOpen(false); onDownloadPdf(delivery.id, delivery.pdf_filename); }}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  data-testid="delivery-download-pdf-btn"
                >
                  <Download className="h-4 w-4 text-blue-500" />
                  Original-PDF öffnen
                </button>
              )}
              <button
                onClick={() => { setMenuOpen(false); navigate('/lieferung/reklamation'); }}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                data-testid="delivery-reklamation-btn"
              >
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Reklamation
              </button>
              <button
                onClick={() => { setMenuOpen(false); onDelete(delivery); }}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                data-testid="delivery-delete-btn"
              >
                <Trash2 className="h-4 w-4" />
                Löschen
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Produkt-Karte ────────────────────────────────────────────────────────────
const ProductCard = ({ product, isSkipMhd, onToggleMhd, isNoMhd, onResetNoMhd, noMhdAutoDetected, noMhdReason }) => {
  const hasEan = product.has_ean && product.ean;
  // MHD-Flag: entweder neues has_no_mhd (fremd_nr basiert) oder Legacy EAN-basiert
  const mhdSkipped = isNoMhd || isSkipMhd;
  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-4 hover:border-primary/40 transition-all" data-testid="product-card">
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="font-medium text-sm text-gray-900 dark:text-white leading-snug line-clamp-2 flex-1">{product.article_name}</p>
        {hasEan ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400 flex-shrink-0 whitespace-nowrap" data-testid="ean-badge-known">
            <Hash className="h-3 w-3" /> EAN
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400 flex-shrink-0 whitespace-nowrap" data-testid="ean-badge-missing">
            <Hash className="h-3 w-3" /> EAN fehlt
          </span>
        )}
      </div>
      {product.fremd_article_number && (
        <p className="text-xs text-gray-400 dark:text-gray-500 font-mono mb-1 truncate">Art-Nr: {product.fremd_article_number}</p>
      )}
      {hasEan && (
        <p className="text-xs text-gray-400 dark:text-gray-500 font-mono mb-2 truncate">{product.ean}</p>
      )}
      {/* Auto-Erkannt Badge */}
      {isNoMhd && noMhdAutoDetected && (
        <div className="flex items-center gap-1 mb-2" data-testid="auto-detected-badge">
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-700">
            <Zap className="h-2.5 w-2.5" /> Auto-Erkannt
          </span>
          {noMhdReason && (
            <span className="text-xs text-gray-400 truncate">{noMhdReason.replace('Auto: ', '')}</span>
          )}
        </div>
      )}
      <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-gray-100 dark:border-gray-800">
        <div className="flex items-center gap-2">
          <BarChart2 className="h-3.5 w-3.5 text-primary flex-shrink-0" />
          <span className="text-xs text-gray-500 dark:text-gray-400">
            <span className="font-semibold text-gray-700 dark:text-gray-300">{product.delivery_count}×</span> geliefert
          </span>
        </div>
        {/* MHD-Toggle: für Produkte MIT EAN (Legacy) ODER mit has_no_mhd Flag */}
        {(hasEan || isNoMhd) && (hasEan ? onToggleMhd : onResetNoMhd) && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (isNoMhd && onResetNoMhd) onResetNoMhd(product);
              else if (hasEan && onToggleMhd) onToggleMhd(product);
            }}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${
              mhdSkipped
                ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-700'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:border-primary/40'
            }`}
            data-testid={mhdSkipped ? 'mhd-skip-active-btn' : 'mhd-skip-inactive-btn'}
            title={mhdSkipped ? 'MHD-Abfrage ist deaktiviert – klicken zum Reaktivieren' : 'MHD-Abfrage aktiv – klicken zum Deaktivieren'}
          >
            {mhdSkipped ? (
              <><SkipForward className="h-3 w-3" /> kein MHD</>
            ) : (
              <><Calendar className="h-3 w-3" /> MHD aktiv</>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Haupt-Komponente ─────────────────────────────────────────────────────────
export default function Lieferungen() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('lieferungen');
  const [locations, setLocations] = useState([]);  // mit Adressdaten
  const [selectedLocation, setSelectedLocation] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [supplierSaving, setSupplierSaving] = useState(false);
  const [deleteSupplierTarget, setDeleteSupplierTarget] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [deliveriesLoading, setDeliveriesLoading] = useState(false);
  const [deleteDeliveryTarget, setDeleteDeliveryTarget] = useState(null);
  const [deletingDelivery, setDeletingDelivery] = useState(false);
  const [deliverySearch, setDeliverySearch] = useState('');
  const [deliveryStatusFilter, setDeliveryStatusFilter] = useState('all');
  const [selectedDeliveries, setSelectedDeliveries] = useState(new Set());
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState(null);
  const [alert, setAlert] = useState(null);
  // Alle Produkte
  const [allProducts, setAllProducts] = useState([]);
  const [pendingReview, setPendingReview] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [productFilter, setProductFilter] = useState('all'); // 'all' | 'with_ean' | 'without_ean'
  const [skipMhdEans, setSkipMhdEans] = useState(new Set());
  const [noMhdFremdNrs, setNoMhdFremdNrs] = useState(new Set()); // has_no_mhd Flag (fremd_nr basiert)
  const [noMhdMeta, setNoMhdMeta] = useState({});  // fremd_nr → {auto_detected, reason}
  const [mhdFilterActive, setMhdFilterActive] = useState(false); // nur Skip-MHD-Produkte anzeigen
  // EAN-Pflege-Modus
  const [eanPflegeActive, setEanPflegeActive] = useState(false);
  const [pflegeCurrentIdx, setPflegeCurrentIdx] = useState(0);
  const [pflege_ean, setPflegeEan] = useState('');
  const [pflege_saving, setPflegeSaving] = useState(false);
  const [pflegeScannerOpen, setPflegeScannerOpen] = useState(false);
  const pflegeInputRef = useRef();
  // Import-Logs
  const [importLogs, setImportLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [pollingNow, setPollingNow] = useState(false);

  const showAlert = useCallback((type, msg) => {
    setAlert({ type, msg });
    setTimeout(() => setAlert(null), 5000);
  }, []);

  // Standorte mit Adressdaten laden (neuer Endpunkt)
  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API}/api/lieferung/locations`, { headers: authHeader() });
        const locs = r.data || [];
        setLocations(locs);
        if (locs.length === 1) setSelectedLocation(locs[0].id);
      } catch {
        // Fallback auf user/locations
        try {
          const r = await axios.get(`${API}/api/user/locations`, { headers: authHeader() });
          setLocations(r.data || []);
        } catch {}
      }
    };
    load();
  }, []);

  const loadSuppliers = useCallback(async () => {
    setSuppliersLoading(true);
    try {
      const r = await axios.get(`${API}/api/lieferung/suppliers`, { headers: authHeader() });
      setSuppliers(r.data || []);
    } catch {} finally { setSuppliersLoading(false); }
  }, []);

  const loadDeliveries = useCallback(async () => {
    setDeliveriesLoading(true);
    try {
      const r = await axios.get(`${API}/api/lieferung/deliveries`, { headers: authHeader() });
      setDeliveries(r.data || []);
    } catch {} finally { setDeliveriesLoading(false); }
  }, []);

  const loadAllProducts = useCallback(async () => {
    setProductsLoading(true);
    try {
      const [productsRes, skipRes, noMhdRes] = await Promise.all([
        axios.get(`${API}/api/lieferung/all-products`, { headers: authHeader() }),
        axios.get(`${API}/api/admin/settings/skip-mhd-eans`, { headers: authHeader() }),
        axios.get(`${API}/api/lieferung/no-mhd-products`, { headers: authHeader() }),
      ]);
      setAllProducts(productsRes.data?.products || []);
      setPendingReview(productsRes.data?.pending_review || []);
      setSkipMhdEans(new Set(skipRes.data?.skip_mhd_eans || []));
      setNoMhdFremdNrs(new Set(noMhdRes.data?.no_mhd_fremd_nrs || []));
      const meta = {};
      (noMhdRes.data?.products || []).forEach(p => {
        if (p.fremd_article_number) meta[p.fremd_article_number] = p;
      });
      setNoMhdMeta(meta);
    } catch {} finally { setProductsLoading(false); }
  }, []);

  // EAN-Pflege: EAN für Produkt speichern + MHD-Auto-Import
  const saveEanForProduct = useCallback(async (product, ean) => {
    if (!ean || !product?.fremd_article_number) return;
    setPflegeSaving(true);
    try {
      const r = await axios.post(`${API}/api/lieferung/ean`, {
        fremd_article_number: product.fremd_article_number,
        ean: ean.trim(),
        article_name: product.article_name,
      }, { headers: authHeader() });
      // Lokalen State aktualisieren
      setAllProducts(prev => prev.map(p =>
        p.fremd_article_number === product.fremd_article_number
          ? { ...p, ean: ean.trim(), has_ean: true }
          : p
      ));
      setPflegeEan('');
      if (r.data?.mhd_created > 0) {
        toast.success(`MHD automatisch angelegt!`, {
          description: r.data.mhd_products.map(m => `${m.product_name} – ${m.expiry_date}`).join(', '),
          duration: 5000,
        });
      } else {
        toast.success(`EAN gespeichert`, { description: product.article_name, duration: 2000 });
      }
      // Nächstes Produkt ohne EAN automatisch auswählen
      const productsWithoutEan = allProducts.filter(p => !p.has_ean && p.fremd_article_number !== product.fremd_article_number);
      if (productsWithoutEan.length === 0) {
        toast.success('Alle EANs erfasst! Pflege abgeschlossen.', { duration: 4000 });
        setEanPflegeActive(false);
      } else {
        setPflegeCurrentIdx(0); // immer erstes verbleibendes
      }
    } catch {
      toast.error('Fehler beim Speichern der EAN');
    } finally {
      setPflegeSaving(false);
      setTimeout(() => pflegeInputRef.current?.focus(), 80);
    }
  }, [allProducts]);

  useEffect(() => { loadSuppliers(); }, [loadSuppliers]);
  useEffect(() => { loadDeliveries(); }, [loadDeliveries]);
  useEffect(() => { if (activeTab === 'produkte') loadAllProducts(); }, [activeTab, loadAllProducts]);

  // has_no_mhd Flag zurücksetzen (fremd_article_number basiert)
  const handleResetNoMhd = useCallback(async (product) => {
    const fremdNr = product.fremd_article_number;
    if (!fremdNr) return;
    try {
      await axios.delete(`${API}/api/lieferung/products/reset-no-mhd/${encodeURIComponent(fremdNr)}`, { headers: authHeader() });
      setNoMhdFremdNrs(prev => { const n = new Set(prev); n.delete(fremdNr); return n; });
      setNoMhdMeta(prev => { const n = {...prev}; delete n[fremdNr]; return n; });
      toast.success('MHD-Abfrage wieder aktiv', { description: product.article_name, duration: 2000 });
    } catch {
      toast.error('Fehler beim Zurücksetzen');
    }
  }, []);

  // MHD-Skip Toggle: EAN hinzufügen oder entfernen
  const handleToggleSkipMhd = useCallback(async (product) => {
    const ean = product.ean;
    if (!ean) return;
    const isSkipped = skipMhdEans.has(ean);
    try {
      if (isSkipped) {
        await axios.delete(`${API}/api/admin/settings/skip-mhd-ean/${encodeURIComponent(ean)}`, { headers: authHeader() });
        setSkipMhdEans(prev => { const n = new Set(prev); n.delete(ean); return n; });
        toast.success('MHD-Abfrage wieder aktiv', { description: product.article_name, duration: 2000 });
      } else {
        await axios.post(`${API}/api/admin/settings/skip-mhd-ean`, { ean }, { headers: authHeader() });
        setSkipMhdEans(prev => new Set([...prev, ean]));
        toast.success('MHD-Abfrage deaktiviert', { description: product.article_name, duration: 2000 });
      }
    } catch {
      toast.error('Fehler beim Speichern');
    }
  }, [skipMhdEans]);

  const loadImportLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const r = await axios.get(`${API}/api/lieferung/import-logs?limit=100`, { headers: authHeader() });
      setImportLogs(r.data || []);
    } catch {} finally { setLogsLoading(false); }
  }, []);

  useEffect(() => { if (activeTab === 'import-log') loadImportLogs(); }, [activeTab, loadImportLogs]);

  const handleTriggerPoll = async () => {
    setPollingNow(true);
    try {
      await axios.post(`${API}/api/lieferung/imap-poll-now`, {}, { headers: authHeader() });
      toast.success('IMAP-Polling gestartet', { description: 'Neue E-Mails werden im Hintergrund verarbeitet. Aktualisieren Sie das Log in ca. 15 Sekunden.' });
      setTimeout(() => loadImportLogs(), 8000);
    } catch (e) {
      toast.error('Fehler beim Starten des Pollings');
    } finally { setPollingNow(false); }
  };

  const handleDownloadLogPdf = async (logId, filename) => {
    try {
      const response = await axios.get(
        `${API}/api/lieferung/import-logs/${logId}/download`,
        { headers: authHeader(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch {
      toast.error('PDF nicht verfügbar', { description: 'Für ältere Einträge ist kein Archiv vorhanden.' });
    }
  };

  const handleDownloadDeliveryPdf = async (deliveryId, filename) => {
    try {
      const response = await axios.get(
        `${API}/api/lieferung/deliveries/${deliveryId}/original-pdf`,
        { headers: authHeader(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch {
      toast.error('Original-PDF nicht verfügbar', { description: 'Nur für per E-Mail importierte Lieferungen vorhanden.' });
    }
  };

  const handleSaveSupplier = async (form) => {
    setSupplierSaving(true);
    try {
      if (editingSupplier) {
        await axios.put(`${API}/api/lieferung/suppliers/${editingSupplier.id}`, form, { headers: authHeader() });
        showAlert('success', 'Lieferant aktualisiert');
      } else {
        await axios.post(`${API}/api/lieferung/suppliers`, form, { headers: authHeader() });
        showAlert('success', 'Lieferant angelegt');
      }
      setShowSupplierModal(false);
      setEditingSupplier(null);
      loadSuppliers();
    } catch (e) {
      showAlert('error', e.response?.data?.detail || 'Fehler beim Speichern');
    } finally { setSupplierSaving(false); }
  };

  const handleDeleteSupplier = async () => {
    try {
      await axios.delete(`${API}/api/lieferung/suppliers/${deleteSupplierTarget.id}`, { headers: authHeader() });
      showAlert('success', 'Lieferant gelöscht');
      setDeleteSupplierTarget(null);
      loadSuppliers();
    } catch (e) {
      showAlert('error', e.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handlePdfUpload = async (file) => {
    const locId = selectedLocation || (locations.length === 1 ? locations[0].id : '');
    if (!locId) { showAlert('error', 'Bitte zuerst einen Standort auswählen'); return; }
    const suppliersForLoc = suppliers.filter(s => s.location_id === locId);
    if (suppliersForLoc.length === 0) {
      showAlert('error', 'Für diesen Standort ist noch kein Lieferant hinterlegt. Bitte zuerst einen Lieferanten anlegen.');
      return;
    }

    setImportLoading(true);
    setImportResult(null);
    setImportError(null);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('location_id', locId);

    try {
      const r = await axios.post(`${API}/api/lieferung/import`, formData, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(r.data);
      showAlert('success', `WBS ${r.data.delivery?.wbs_number || ''} importiert – ${r.data.items_count} Artikel`);
      loadDeliveries();
      if (r.data.delivery_id) {
        setTimeout(() => navigate(`/lieferung/${r.data.delivery_id}`), 1500);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'Import fehlgeschlagen';
      setImportError(msg);
      showAlert('error', msg);
    } finally { setImportLoading(false); }
  };

  const handleDeleteDelivery = async () => {
    if (!deleteDeliveryTarget) return;
    setDeletingDelivery(true);
    try {
      await axios.delete(`${API}/api/lieferung/deliveries/${deleteDeliveryTarget.id}`, { headers: authHeader() });
      showAlert('success', 'Lieferung gelöscht');
      setDeleteDeliveryTarget(null);
      loadDeliveries();
    } catch (e) {
      showAlert('error', e.response?.data?.detail || 'Fehler beim Löschen');
    } finally { setDeletingDelivery(false); }
  };

  const handleReopenDelivery = async (delivery) => {
    if (!window.confirm(`Lieferung "${delivery.wbs_number}" wiedereröffnen?\nDer Status wird zurückgesetzt und Artikel können erneut bearbeitet werden.`)) return;
    try {
      await axios.patch(`${API}/api/lieferung/deliveries/${delivery.id}/reopen`, {}, { headers: authHeader() });
      toast.success('Lieferung erfolgreich wiedereröffnet');
      loadDeliveries();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Wiedereröffnen');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedDeliveries.size === 0) return;
    if (!window.confirm(`${selectedDeliveries.size} Lieferung(en) unwiderruflich löschen?\n\nAlle zugehörigen Artikel, Pakete und Import-Logs werden ebenfalls entfernt.`)) return;
    setBulkDeleting(true);
    try {
      const r = await axios.post(`${API}/api/lieferung/deliveries/bulk-delete`, { delivery_ids: [...selectedDeliveries] }, { headers: authHeader() });
      toast.success(`${r.data.deleted} Lieferung(en) gelöscht`);
      setSelectedDeliveries(new Set());
      setBulkDeleteMode(false);
      loadDeliveries();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Löschen');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleSelectDelivery = (id) => {
    setSelectedDeliveries(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedDeliveries(new Set(filteredDeliveries.map(d => d.id)));
    } else {
      setSelectedDeliveries(new Set());
    }
  };

  const getLocationName = id => locations.find(l => l.id === id)?.name || '';
  const canManage = user?.role === 'admin' || user?.role === 'stationsleitung';
  const isSuperadmin = user?.role === 'superadmin';
  const tabs = [
    { key: 'lieferungen', label: 'Lieferungen', icon: Truck },
    { key: 'lieferanten', label: 'Lieferanten', icon: Building2 },
    { key: 'produkte', label: 'Alle Produkte', icon: ShoppingBag },
    { key: 'import-log', label: 'Import-Log', icon: Activity },
  ];

  // Gefilterte Lieferungsliste
  const filteredDeliveries = deliveries.filter(d => {
    const q = deliverySearch.trim().toLowerCase();
    const matchesSearch = !q ||
      d.wbs_number?.toLowerCase().includes(q) ||
      d.supplier_name?.toLowerCase().includes(q);
    const matchesStatus = deliveryStatusFilter === 'all' || d.status === deliveryStatusFilter;
    return matchesSearch && matchesStatus;
  });

  // Gefilterte Produktliste
  const filteredProducts = allProducts.filter(p => {
    const q = productSearch.trim().toLowerCase();
    const matchesSearch = !q ||
      p.article_name?.toLowerCase().includes(q) ||
      p.fremd_article_number?.toLowerCase().includes(q) ||
      p.ean?.toLowerCase().includes(q);
    const matchesFilter =
      productFilter === 'all' ||
      (productFilter === 'with_ean' && p.has_ean) ||
      (productFilter === 'without_ean' && !p.has_ean);
    const matchesMhdFilter = !mhdFilterActive || (p.ean && skipMhdEans.has(p.ean)) || (p.fremd_article_number && noMhdFremdNrs.has(p.fremd_article_number));
    return matchesSearch && matchesFilter && matchesMhdFilter;
  });

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">

        {alert && (
          <div className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium ${alert.type === 'success' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'}`} data-testid="alert-message">
            {alert.type === 'success' ? <CheckCircle className="h-4 w-4 flex-shrink-0" /> : <AlertCircle className="h-4 w-4 flex-shrink-0" />}
            {alert.msg}
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Truck className="h-6 w-6 text-primary" />
              Lieferungen
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Lieferschein-Automator – WBS importieren & prüfen</p>
          </div>
          {locations.length > 1 && (
            <select value={selectedLocation} onChange={e => setSelectedLocation(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 min-w-[180px]" data-testid="location-selector">
              <option value="">Alle Standorte</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          )}
        </div>

        {/* Tabs – horizontales Scrollen auf Mobile */}
        <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700 overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
          {tabs.map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setActiveTab(key)}
              className={`flex items-center gap-1.5 px-3 sm:px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors border-b-2 -mb-px whitespace-nowrap flex-shrink-0 ${activeTab === key ? 'border-primary text-primary bg-primary/5' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}
              data-testid={`tab-${key}`}>
              <Icon className="h-4 w-4 flex-shrink-0" />
              <span>{label}</span>
              {key === 'lieferanten' && suppliers.length > 0 && <span className="px-1.5 py-0.5 rounded-full text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">{suppliers.length}</span>}
              {key === 'lieferungen' && deliveries.length > 0 && <span className="px-1.5 py-0.5 rounded-full text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">{deliveries.length}</span>}
              {key === 'produkte' && allProducts.length > 0 && <span className="px-1.5 py-0.5 rounded-full text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">{allProducts.length}</span>}
              {key === 'import-log' && importLogs.length > 0 && <span className="px-1.5 py-0.5 rounded-full text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">{importLogs.length}</span>}
            </button>
          ))}
        </div>

        {/* ── LIEFERUNGEN TAB ── */}
        {activeTab === 'lieferungen' && (
          <div className="space-y-4">
            {canManage && (
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl p-5">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">WBS-PDF importieren</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                  Lekkerland Warenbegleitschein hochladen – Kundennummer und Adresse werden automatisch geprüft.
                </p>
                {locations.length > 1 && (
                  <div className="mb-4">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Standort</label>
                    <select value={selectedLocation} onChange={e => setSelectedLocation(e.target.value)} className="w-full sm:w-64 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/50" data-testid="import-location-select">
                      <option value="">Standort wählen...</option>
                      {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                  </div>
                )}
                <UploadArea onFile={handlePdfUpload} loading={importLoading} />
                {importResult && (
                  <div className="mt-4 p-4 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800" data-testid="import-success">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm font-semibold text-green-700 dark:text-green-300">Import erfolgreich – leite zur Checkliste weiter...</span>
                    </div>
                    <div className="text-xs text-green-600 dark:text-green-400 space-y-0.5">
                      <p>Lieferant: <strong>{importResult.supplier_name}</strong> | WBS: <strong>{importResult.delivery?.wbs_number}</strong> | {importResult.items_count} Artikel</p>
                      {importResult.ai_assisted && (
                        <p className="text-blue-600 dark:text-blue-400 flex items-center gap-1">
                          <span className="inline-flex items-center gap-1 bg-blue-100 dark:bg-blue-900/40 px-2 py-0.5 rounded-full text-xs font-semibold">
                            KI-unterstützt
                          </span>
                          Unbekanntes Lieferantenformat – Gemini Flash hat die Artikel extrahiert. Bitte Artikelnamen und Mengen prüfen.
                        </p>
                      )}
                      {importResult.address_warning && <p className="text-amber-600 dark:text-amber-400">⚠ {importResult.address_warning}</p>}
                      {importResult.parse_warnings?.filter(w => !w.includes('KI-unterstützt')).map((w, i) => (
                        <p key={i} className="text-amber-600 dark:text-amber-400">⚠ {w}</p>
                      ))}
                    </div>
                  </div>
                )}
                {importError && (
                  <div className="mt-4 p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800" data-testid="import-error">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-red-700 dark:text-red-300 mb-0.5">Import fehlgeschlagen</p>
                        <p className="text-xs text-red-600 dark:text-red-400">{importError}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-3">
              {/* Header */}
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Lieferungen ({filteredDeliveries.length}{filteredDeliveries.length !== deliveries.length ? ` von ${deliveries.length}` : ''})
                </h3>
                <div className="flex items-center gap-2">
                  {!bulkDeleteMode && canManage && deliveries.length > 0 && (
                    <button
                      onClick={() => { setBulkDeleteMode(true); setSelectedDeliveries(new Set()); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-red-300 hover:text-red-600 dark:hover:border-red-700 dark:hover:text-red-400 transition-colors"
                      data-testid="bulk-select-btn"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Mehrfachauswahl
                    </button>
                  )}
                  <button onClick={loadDeliveries} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" data-testid="refresh-deliveries-btn">
                    <RefreshCw className={`h-4 w-4 ${deliveriesLoading ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              </div>

              {/* Suchleiste + Statusfilter */}
              {deliveries.length > 0 && (
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    <input
                      type="text"
                      value={deliverySearch}
                      onChange={e => setDeliverySearch(e.target.value)}
                      placeholder="WBS-Nr. oder Lieferant suchen..."
                      className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                      data-testid="delivery-search-input"
                    />
                  </div>
                  <select
                    value={deliveryStatusFilter}
                    onChange={e => setDeliveryStatusFilter(e.target.value)}
                    className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                    data-testid="delivery-status-filter"
                  >
                    <option value="all">Alle Status</option>
                    <option value="imported">Importiert</option>
                    <option value="in_progress">In Bearbeitung</option>
                    <option value="completed">Abgeschlossen</option>
                  </select>
                </div>
              )}

              {/* Bulk-Delete Toolbar */}
              {bulkDeleteMode && (
                <div className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl" data-testid="bulk-delete-toolbar">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={filteredDeliveries.length > 0 && selectedDeliveries.size === filteredDeliveries.length}
                      onChange={handleSelectAll}
                      className="w-4 h-4 rounded border-gray-300 accent-primary cursor-pointer"
                      data-testid="select-all-checkbox"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {selectedDeliveries.size === 0
                        ? 'Keine ausgewählt'
                        : `${selectedDeliveries.size} ausgewählt`}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedDeliveries.size > 0 && (
                      <button
                        onClick={handleBulkDelete}
                        disabled={bulkDeleting}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
                        data-testid="bulk-delete-confirm-btn"
                      >
                        {bulkDeleting
                          ? <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> Löschen...</>
                          : <><Trash2 className="h-3.5 w-3.5" /> {selectedDeliveries.size} löschen</>}
                      </button>
                    )}
                    <button
                      onClick={() => { setBulkDeleteMode(false); setSelectedDeliveries(new Set()); }}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                      data-testid="bulk-cancel-btn"
                    >
                      <X className="h-3.5 w-3.5" /> Abbrechen
                    </button>
                  </div>
                </div>
              )}

              {/* Liste */}
              {deliveriesLoading ? (
                <div className="flex items-center justify-center py-10"><RefreshCw className="h-5 w-5 animate-spin text-primary" /></div>
              ) : deliveries.length === 0 ? (
                <div className="text-center py-12 bg-white dark:bg-gray-900 border border-dashed border-gray-200 dark:border-gray-700 rounded-2xl" data-testid="empty-deliveries">
                  <Truck className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Noch keine Lieferungen</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">Laden Sie ein WBS-PDF hoch, um zu beginnen</p>
                </div>
              ) : filteredDeliveries.length === 0 ? (
                <div className="text-center py-10 text-gray-400 dark:text-gray-500 text-sm" data-testid="no-filter-results">
                  Keine Lieferungen für diesen Filter gefunden.
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredDeliveries.map(d => (
                    <DeliveryRow
                      key={d.id}
                      delivery={d}
                      onDelete={setDeleteDeliveryTarget}
                      onDownloadPdf={handleDownloadDeliveryPdf}
                      onReopen={handleReopenDelivery}
                      userRole={user?.role}
                      selectMode={bulkDeleteMode}
                      selected={selectedDeliveries.has(d.id)}
                      onSelect={handleSelectDelivery}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── LIEFERANTEN TAB ── */}
        {activeTab === 'lieferanten' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500 dark:text-gray-400">{suppliers.length} {suppliers.length === 1 ? 'Lieferant' : 'Lieferanten'} hinterlegt</p>
              {canManage && (
                <button onClick={() => { setEditingSupplier(null); setShowSupplierModal(true); }} className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:opacity-90 transition-all" data-testid="new-supplier-btn">
                  <Plus className="h-4 w-4" /> Neuer Lieferant
                </button>
              )}
            </div>
            {suppliersLoading ? (
              <div className="flex items-center justify-center py-10"><RefreshCw className="h-5 w-5 animate-spin text-primary" /></div>
            ) : suppliers.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-gray-900 border border-dashed border-gray-200 dark:border-gray-700 rounded-2xl" data-testid="empty-suppliers">
                <Building2 className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Noch kein Lieferant angelegt</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">Legen Sie zuerst Ihren Lieferanten an</p>
                {canManage && (
                  <button onClick={() => { setEditingSupplier(null); setShowSupplierModal(true); }} className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:opacity-90" data-testid="empty-new-supplier-btn">
                    <Plus className="h-4 w-4" /> Lieferant anlegen
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {suppliers.map(s => (
                  <SupplierCard key={s.id} supplier={s} locationName={locations.length > 1 ? getLocationName(s.location_id) : ''} onEdit={sup => { setEditingSupplier(sup); setShowSupplierModal(true); }} onDelete={setDeleteSupplierTarget} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── ALLE PRODUKTE TAB ── */}
        {activeTab === 'produkte' && (() => {
          // Produkte ohne EAN für Pflege-Modus
          const productsWithoutEan = allProducts.filter(p => !p.has_ean);
          const currentPflegeProduct = productsWithoutEan[pflegeCurrentIdx] || productsWithoutEan[0];

          return (
          <div className="space-y-5" data-testid="produkte-tab">
            {/* Superadmin: Neue Produkte zur Prüfung */}
            {isSuperadmin && pendingReview.length > 0 && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <AlertOctagon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                    Neue Produkte zur Prüfung ({pendingReview.length})
                  </h3>
                </div>
                <p className="text-xs text-amber-700 dark:text-amber-400 mb-4">
                  Diese Produkte wurden von Kunden geliefert, haben aber noch keine EAN. Bitte prüfen und ggf. in die Master-Datenbank aufnehmen.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {pendingReview.map((p, i) => (
                    <div key={i} className="bg-white dark:bg-gray-900 border border-amber-200 dark:border-amber-700 rounded-xl p-3">
                      <p className="font-medium text-sm text-gray-900 dark:text-white truncate">{p.article_name}</p>
                      {p.fremd_article_number && <p className="text-xs text-gray-400 font-mono mt-0.5">Art-Nr: {p.fremd_article_number}</p>}
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">{p.delivery_count}× geliefert</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* EAN-Pflege-Modus Panel */}
            {eanPflegeActive && (
              <div className="bg-gradient-to-br from-primary/10 to-primary/5 border-2 border-primary/30 rounded-2xl p-3 sm:p-5" data-testid="ean-pflege-panel">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse flex-shrink-0" />
                    <span className="font-semibold text-sm text-gray-900 dark:text-white whitespace-nowrap">EAN-Pflege aktiv</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 px-2 py-0.5 rounded-full whitespace-nowrap hidden xs:inline">
                      {productsWithoutEan.length} ohne EAN
                    </span>
                  </div>
                  <button onClick={() => { setEanPflegeActive(false); setPflegeEan(''); }} className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 flex items-center gap-1 flex-shrink-0 ml-2">
                    <X className="h-3.5 w-3.5" /> Beenden
                  </button>
                </div>

                {currentPflegeProduct ? (
                  <>
                    {/* Aktuelles Produkt – kompakt */}
                    <div className="bg-white dark:bg-gray-900 border border-primary/40 rounded-xl p-3 mb-3">
                      <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5 uppercase tracking-wider">Aktuelles Produkt</p>
                      <p className="font-semibold text-sm text-gray-900 dark:text-white">{currentPflegeProduct.article_name}</p>
                      <p className="text-xs text-gray-400 font-mono">{currentPflegeProduct.fremd_article_number} · {currentPflegeProduct.delivery_count}× geliefert</p>
                    </div>

                    {/* Scan-Input – kompakt */}
                    <form onSubmit={e => { e.preventDefault(); if (pflege_ean.trim().length >= 8) saveEanForProduct(currentPflegeProduct, pflege_ean.trim()); }}
                      className="space-y-2">
                      <div className="flex gap-2">
                        <input
                          ref={pflegeInputRef}
                          type="text"
                          inputMode="numeric"
                          value={pflege_ean}
                          onChange={e => setPflegeEan(e.target.value)}
                          autoFocus
                          placeholder="EAN scannen oder tippen…"
                          maxLength={14}
                          className="flex-1 min-w-0 px-3 py-3 rounded-xl border-2 border-primary/40 bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-mono text-base text-center focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary tracking-widest min-h-[48px]"
                          data-testid="pflege-ean-input"
                        />
                        <button type="button" onClick={() => setPflegeScannerOpen(true)}
                          className="px-3 py-3 bg-white dark:bg-gray-800 border-2 border-primary/30 rounded-xl text-primary hover:border-primary transition-colors flex-shrink-0 min-h-[48px]"
                          data-testid="pflege-camera-btn">
                          <ScanLine className="h-5 w-5" />
                        </button>
                      </div>
                      <p className="text-xs text-center text-gray-500 dark:text-gray-400 hidden sm:block">
                        Bluetooth-Scanner: einfach scannen – Feld ist bereit
                      </p>
                      <div className="flex flex-col gap-2">
                        <button type="submit" disabled={pflege_ean.trim().length < 8 || pflege_saving}
                          className="w-full px-4 py-3 bg-primary text-white rounded-xl text-sm font-semibold hover:opacity-90 disabled:opacity-40 transition-all flex items-center justify-center gap-2 min-h-[48px]"
                          data-testid="pflege-save-btn">
                          {pflege_saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                          EAN speichern & weiter
                        </button>
                        {productsWithoutEan.length > 1 && (
                          <button type="button"
                            onClick={() => { setPflegeEan(''); setPflegeCurrentIdx(i => (i + 1) % productsWithoutEan.length); setTimeout(() => pflegeInputRef.current?.focus(), 80); }}
                            className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 rounded-xl text-sm hover:border-primary/50 transition-colors flex items-center justify-center gap-1.5 min-h-[48px]"
                            data-testid="pflege-skip-btn">
                            Überspringen <ChevronRight className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </form>

                    {/* Warteschlange – horizontal scrollbar, eine Zeile */}
                    {productsWithoutEan.length > 1 && (
                      <div className="mt-3 flex gap-2 overflow-x-auto scrollbar-hide pb-1">
                        {productsWithoutEan.slice(0, 8).map((p) => (
                          <button key={p.fremd_article_number}
                            onClick={() => { setPflegeCurrentIdx(productsWithoutEan.indexOf(p)); setPflegeEan(''); setTimeout(() => pflegeInputRef.current?.focus(), 80); }}
                            className={`text-xs px-2.5 py-1.5 rounded-lg border transition-colors flex-shrink-0 ${p.fremd_article_number === currentPflegeProduct?.fremd_article_number ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:border-primary/50'}`}>
                            {p.article_name.split(' ').slice(0, 2).join(' ')}
                          </button>
                        ))}
                        {productsWithoutEan.length > 8 && (
                          <span className="text-xs text-gray-400 self-center flex-shrink-0 pr-1">+{productsWithoutEan.length - 8}</span>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8">
                    <CheckCircle className="h-10 w-10 text-green-500 mx-auto mb-2" />
                    <p className="font-semibold text-gray-900 dark:text-white">Alle EANs erfasst!</p>
                    <p className="text-xs text-gray-500 mt-1">Deine Produktdatenbank ist vollständig.</p>
                  </div>
                )}
              </div>
            )}

            {/* Such- und Filter-Leiste */}
            <div className="flex flex-col gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Produktname, Art-Nr oder EAN suchen..."
                  value={productSearch}
                  onChange={e => setProductSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/40"
                  data-testid="product-search-input"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                {[['all', 'Alle'], ['with_ean', 'Mit EAN'], ['without_ean', 'Ohne EAN']].map(([val, lbl]) => (
                  <button key={val} onClick={() => setProductFilter(val)}
                    className={`px-3 py-3 rounded-xl text-xs font-medium transition-colors min-h-[44px] ${productFilter === val ? 'bg-primary text-white' : 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-primary/50'}`}
                    data-testid={`filter-${val}`}>
                    {lbl}
                  </button>
                ))}
                <button
                  onClick={() => setMhdFilterActive(prev => !prev)}
                  className={`px-3 py-3 rounded-xl text-xs font-medium transition-colors min-h-[44px] flex items-center gap-1.5 ${mhdFilterActive ? 'bg-amber-500 text-white' : 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-amber-400'}`}
                  data-testid="filter-skip-mhd"
                  title="Nur Produkte anzeigen, bei denen MHD-Abfrage deaktiviert ist"
                >
                  <SkipForward className="h-3.5 w-3.5" /> kein MHD
                </button>
                {!eanPflegeActive && productsWithoutEan.length > 0 && (
                  <button
                    onClick={() => { setEanPflegeActive(true); setProductFilter('without_ean'); setPflegeCurrentIdx(0); setPflegeEan(''); setTimeout(() => pflegeInputRef.current?.focus(), 200); }}
                    className="px-3 py-3 rounded-xl text-xs font-semibold bg-primary text-white hover:opacity-90 transition-all flex items-center gap-1.5 min-h-[44px]"
                    data-testid="start-ean-pflege-btn">
                    <Zap className="h-3.5 w-3.5" /> EAN-Pflege starten
                  </button>
                )}
                <button onClick={loadAllProducts} className="px-3 py-3 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-500 hover:text-primary hover:border-primary/50 transition-colors min-h-[44px]">
                  <RefreshCw className={`h-4 w-4 ${productsLoading ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>

            {/* Statistik-Leiste */}
            <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
              <span><span className="font-semibold text-gray-700 dark:text-gray-300">{allProducts.length}</span> Produkte gesamt</span>
              <span><span className="font-semibold text-green-600">{allProducts.filter(p => p.has_ean).length}</span> mit EAN</span>
              <span><span className="font-semibold text-amber-600">{allProducts.filter(p => !p.has_ean).length}</span> ohne EAN</span>
              {(skipMhdEans.size + noMhdFremdNrs.size) > 0 && (
                <span><span className="font-semibold text-amber-500">{skipMhdEans.size + noMhdFremdNrs.size}</span> kein MHD</span>
              )}
            </div>

            {/* Produktliste */}
            {productsLoading ? (
              <div className="flex items-center justify-center py-16">
                <RefreshCw className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-16 bg-white dark:bg-gray-900 border border-dashed border-gray-200 dark:border-gray-700 rounded-2xl" data-testid="empty-products">
                <ShoppingBag className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  {allProducts.length === 0 ? 'Noch keine Produkte importiert' : 'Keine Produkte gefunden'}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  {allProducts.length === 0 ? 'Importiere zuerst einen Lieferschein.' : 'Suche anpassen oder Filter zurücksetzen.'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredProducts.map((p, i) => (
                  <div key={i}
                    onClick={eanPflegeActive && !p.has_ean ? () => { const idx = allProducts.filter(x => !x.has_ean).indexOf(p); setPflegeCurrentIdx(idx >= 0 ? idx : 0); setPflegeEan(''); setTimeout(() => pflegeInputRef.current?.focus(), 80); window.scrollTo({top: 0, behavior: 'smooth'}); } : undefined}
                    className={eanPflegeActive && !p.has_ean && p.fremd_article_number === currentPflegeProduct?.fremd_article_number ? 'ring-2 ring-primary rounded-xl cursor-pointer' : eanPflegeActive && !p.has_ean ? 'cursor-pointer' : ''}>
                    <ProductCard
                      product={p}
                      isSkipMhd={!!(p.ean && skipMhdEans.has(p.ean))}
                      onToggleMhd={handleToggleSkipMhd}
                      isNoMhd={!!(p.fremd_article_number && noMhdFremdNrs.has(p.fremd_article_number))}
                      onResetNoMhd={handleResetNoMhd}
                      noMhdAutoDetected={noMhdMeta[p.fremd_article_number]?.auto_detected}
                      noMhdReason={noMhdMeta[p.fremd_article_number]?.auto_detected_reason}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Kamera-Scanner für EAN-Pflege */}
            {pflegeScannerOpen && (
              <BarcodeScanner
                onDetected={code => { setPflegeScannerOpen(false); if (code && code.trim().length >= 8) saveEanForProduct(currentPflegeProduct, code.trim()); }}
                onClose={() => setPflegeScannerOpen(false)}
              />
            )}
          </div>
          );
        })()}
      </div>

      {/* ── IMPORT-LOG TAB ── */}
        {activeTab === 'import-log' && (
          <div className="space-y-4" data-testid="import-log-tab">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Automatische E-Mail-Imports (IMAP-Poller alle 5 Minuten)
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={loadImportLogs}
                  disabled={logsLoading}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                  data-testid="refresh-logs-btn"
                >
                  <RefreshCw className={`h-4 w-4 ${logsLoading ? 'animate-spin' : ''}`} />
                  Aktualisieren
                </button>
                {canManage && (
                  <button
                    onClick={handleTriggerPoll}
                    disabled={pollingNow}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-all"
                    data-testid="trigger-poll-btn"
                  >
                    <Play className={`h-4 w-4 ${pollingNow ? 'animate-pulse' : ''}`} />
                    {pollingNow ? 'Polling läuft...' : 'Jetzt prüfen'}
                  </button>
                )}
              </div>
            </div>

            {/* Log-Tabelle */}
            {logsLoading ? (
              <div className="flex items-center justify-center py-10"><RefreshCw className="h-5 w-5 animate-spin text-primary" /></div>
            ) : importLogs.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-gray-900 border border-dashed border-gray-200 dark:border-gray-700 rounded-2xl" data-testid="empty-import-logs">
                <Activity className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Noch keine Import-Aktivitäten</p>
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  Der Poller prüft automatisch alle 5 Minuten auf neue E-Mails.
                </p>
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                        <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300 text-xs uppercase tracking-wider w-24">Status</th>
                        <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300 text-xs uppercase tracking-wider">Zeitpunkt</th>
                        <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300 text-xs uppercase tracking-wider">Lieferant</th>
                        <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300 text-xs uppercase tracking-wider hidden md:table-cell">Datei</th>
                        <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300 text-xs uppercase tracking-wider">Ergebnis</th>
                        <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300 text-xs uppercase tracking-wider w-16"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {importLogs.map((log) => (
                        <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors" data-testid="import-log-row">
                          <td className="px-4 py-3">
                            <ImportLogStatusBadge status={log.status} />
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {formatDateTimeShort(log.created_at)}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-800 dark:text-gray-200">
                            <div className="flex items-center gap-2">
                              {log.supplier_name || <span className="text-gray-400">—</span>}
                              <MatchMethodBadge method={log.match_method} />
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 hidden md:table-cell max-w-[160px] truncate" title={log.pdf_filename}>
                            {log.pdf_filename || '—'}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-300 max-w-[280px]">
                            <span className="line-clamp-2">{log.message}</span>
                            {log.delivery_id && (
                              <button
                                onClick={() => window.location.href = `/lieferung/${log.delivery_id}`}
                                className="mt-0.5 inline-flex items-center gap-1 text-primary text-xs hover:underline"
                              >
                                <Eye className="h-3 w-3" /> Lieferung öffnen
                              </button>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {log.archived_pdf_b64 && (
                              <button
                                onClick={() => handleDownloadLogPdf(log.id, log.pdf_filename)}
                                className="p-1.5 rounded-lg text-gray-400 hover:text-primary hover:bg-primary/10 transition-colors"
                                title={`PDF herunterladen: ${log.pdf_filename || 'lieferschein.pdf'}`}
                                data-testid="download-log-pdf-btn"
                              >
                                <Download className="h-4 w-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

      {/* ── MODALS ── */}
      {showSupplierModal && (
        <Modal title={editingSupplier ? 'Lieferant bearbeiten' : 'Neuer Lieferant'} onClose={() => { setShowSupplierModal(false); setEditingSupplier(null); }} size="lg">
          <SupplierForm
            initial={editingSupplier}
            locations={locations}
            currentUserName={user?.name || ''}
            onSave={handleSaveSupplier}
            onClose={() => { setShowSupplierModal(false); setEditingSupplier(null); }}
            saving={supplierSaving}
          />
        </Modal>
      )}

      {deleteSupplierTarget && (
        <Modal title="Lieferant löschen" onClose={() => setDeleteSupplierTarget(null)} size="sm">
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">Möchten Sie <strong>{deleteSupplierTarget.name}</strong> wirklich löschen?</p>
          <div className="flex justify-end gap-3">
            <button onClick={() => setDeleteSupplierTarget(null)} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">Abbrechen</button>
            <button onClick={handleDeleteSupplier} className="px-4 py-2 rounded-lg text-sm font-medium bg-red-500 text-white hover:bg-red-600 transition-colors" data-testid="confirm-delete-supplier-btn">Löschen</button>
          </div>
        </Modal>
      )}

      {deleteDeliveryTarget && (
        <Modal title="Lieferung löschen" onClose={() => setDeleteDeliveryTarget(null)} size="sm">
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">Lieferung <strong>WBS {deleteDeliveryTarget.wbs_number || '—'}</strong> und alle Artikel löschen?</p>
          <div className="flex justify-end gap-3">
            <button onClick={() => setDeleteDeliveryTarget(null)} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">Abbrechen</button>
            <button onClick={handleDeleteDelivery} disabled={deletingDelivery} className="px-4 py-2 rounded-lg text-sm font-medium bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 transition-colors" data-testid="confirm-delete-delivery-btn">
              {deletingDelivery ? 'Wird gelöscht...' : 'Löschen'}
            </button>
          </div>
        </Modal>
      )}
    </DashboardLayout>
  );
}
